import { CopyButton } from "@ekafyi/viteui-lib/components";
import type { CopyButtonStateProp } from "@ekafyi/viteui-lib/components";
import { MDXRemote } from "next-mdx-remote";
import type { ComponentPropsWithoutRef } from "react";
import { Fragment, useRef, useState } from "react";
import type { parseMDX } from "@/lib/mdx";
import CustomImage from "./CustomImage";

// import dynamic from "next/dynamic"; // https://nextjs.org/docs/advanced-features/dynamic-import
// const CopyButton = dynamic(() => import("@ekafyi/viteui-lib/components/Button/CopyButton"));

// import { lazy, Suspense } from "react"; // <Suspense><CopyButton /></Suspense>
// const CopyButton = lazy(() => import("@ekafyi/viteui-lib/components/Button/CopyButton"));

const getCalloutType = (children: ComponentPropsWithoutRef<"blockquote">["children"]) => {
  if (Array.isArray(children) && children.length > 1) {
    let firstLine = "";
    if (typeof children[1].props.children === "string") {
      firstLine = children[1].props.children.split("\n")[0];
    } else if (Array.isArray(children[1].props.children)) {
      firstLine = children[1].props.children[0].split("\n")[0];
    }
    if (firstLine && firstLine.startsWith("[!") && firstLine.endsWith("]")) {
      return firstLine.replace("[!", "").replace("]", "").toLowerCase();
    }
  }
  return null;
};

const CustomA = ({ href, ...restProps }: ComponentPropsWithoutRef<"a">) => {
  if (!href) return null;
  // External links.
  if (href.startsWith("https://")) {
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return <a rel="external" href={href} {...restProps} />;
  }
  // Links to other (non-notes) parts of this site.
  if (href.split("/").length > 2) {
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return <a href={href} {...restProps} />;
  }
  const BASE_PATH = "notes";
  // TODO (later) handle HoverCard notes link
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  return <a href={`/${BASE_PATH}${href}`} {...restProps} />;
};

const CustomBlockquote = (props: ComponentPropsWithoutRef<"blockquote">) => {
  const calloutType = getCalloutType(props.children);
  if (calloutType) {
    const customProps = {
      "aria-label": calloutType,
      className: "not-prose",
    };
    return <blockquote {...customProps} {...props} />;
  }
  return <blockquote {...props} />;
};

const CustomP = (props: ComponentPropsWithoutRef<"p">) => {
  const { children, ...restProps } = props;

  let firstLine = "";
  if (typeof children === "string") {
    firstLine = children.split("\n")[0];
  } else if (Array.isArray(children) && typeof children[0] === "string") {
    firstLine = children[0].split("\n")[0];
  }
  if (firstLine && firstLine.startsWith("[!") && firstLine.endsWith("]")) {
    const calloutType = firstLine.replace("[!", "").replace("]", "").toLowerCase();
    let newProps = {};
    if (typeof children === "string") {
      newProps = { ...restProps, children: children.replace(`[!${calloutType}]\n`, "") };
    } else if (Array.isArray(children) && typeof children[0] === "string") {
      const newChildren = children.map((child, idx) =>
        idx === 0 ? children[0].replace(`[!${calloutType}]\n`, "") : child
      );
      newProps = { ...restProps, children: newChildren };
    }
    return (
      <>
        <div data-callout-title={calloutType}>{calloutType}</div>
        <p {...newProps} />
      </>
    );
  }
  return <p {...props} />;
};

const CustomPre = (props: ComponentPropsWithoutRef<"pre">) => {
  const preRef = useRef<HTMLPreElement>(null);

  const [copyState, setCopyState] = useState<CopyButtonStateProp["copyState"]>("initial");

  const handleCopy = () => {
    if (preRef && preRef.current) {
      try {
        navigator.clipboard.writeText(preRef.current.innerText);
        setCopyState("copied");
        setTimeout(() => { setCopyState("initial") }, 2000); // prettier-ignore
      } catch (error) {
        setCopyState("error");
        console.error(error);
      }
    } else {
      console.warn("Cannot copy, element ref missing.");
    }
  };

  const Wrapper = props.hasOwnProperty("data-theme") ? Fragment : "div";
  const wrapperProps = props.hasOwnProperty("data-theme") ? {} : { "data-code-fragment": true };
  return (
    <Wrapper {...wrapperProps}>
      <CopyButton onClick={handleCopy} copyState={copyState} />
      <pre ref={preRef} {...props} />
    </Wrapper>
  );
};

const components = {
  a: (props: ComponentPropsWithoutRef<"a">) => <CustomA {...props} />,
  blockquote: (props: ComponentPropsWithoutRef<"blockquote">) => <CustomBlockquote {...props} />,
  img: (props: ComponentPropsWithoutRef<"img">) => <CustomImage {...props} />,
  p: (props: ComponentPropsWithoutRef<"p">) => <CustomP {...props} />,
  pre: (props: ComponentPropsWithoutRef<"pre">) => <CustomPre {...props} />,
};

interface MDXRendererProps {
  source: Awaited<ReturnType<typeof parseMDX>>;
}

const MDXRenderer = ({ source }: MDXRendererProps) => {
  return <MDXRemote {...source} components={components} />;
};

export default MDXRenderer;
